import { getDataPointFromTooltipContext } from "utils/charts";
import { renderToString } from "react-dom/server.browser";
import { ChangeArrowDown, ChangeArrowUp } from "common/Icons/Icons";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { getIsDarkMode } from "utils/misc";
import { DEFAULT_CHART_TOOLTIP_MARGIN } from "common/constants";
import { formatDate } from "@shared/utils/date";
import { CHART_TOOLTIP_ID } from "src/testIds.ts";
import {
  activateChartTooltip,
  deactivateChartTooltip,
} from "common/Charts/tooltipManager.ts";
import { escapeHtml, safeCssColor } from "utils/html";

const getTooltipTop = (
  chartTop,
  chartHeight,
  tooltipHeight,
  tooltipCaretY,
  verticalCenter,
  _marginFromCaret
) => {
  if (verticalCenter) {
    return chartTop + chartHeight / 2 - tooltipHeight / 2;
  }

  let tooltipTop = chartTop + _marginFromCaret + tooltipCaretY;
  const chartBottom = chartTop + chartHeight;
  let overflowCorrection = tooltipTop + tooltipHeight - chartBottom + 40;
  if (overflowCorrection > 0) {
    tooltipTop -= overflowCorrection;
  }
  return tooltipTop;
};

const getTooltipLeft = (
  chartLeft,
  chartWidth,
  tooltipWidth,
  tooltipCaretX,
  _marginFromCaret
) => {
  let tooltipLeft = chartLeft + _marginFromCaret + tooltipCaretX;
  const chartRight = chartLeft + chartWidth;
  const overflow = tooltipLeft + tooltipWidth - chartRight > 0;
  if (overflow) {
    tooltipLeft = Math.max(
      chartLeft + tooltipCaretX - _marginFromCaret - tooltipWidth,
      chartLeft
    );
  }
  return tooltipLeft;
};

const getChartOrigin = (chartPosition, parentComponent) => {
  if (parentComponent === document.body) {
    return {
      top: chartPosition.top + window.pageYOffset,
      left: chartPosition.left + window.pageXOffset,
    };
  }
  const parentPosition = parentComponent.getBoundingClientRect();
  return {
    top: chartPosition.top - parentPosition.top + parentComponent.scrollTop,
    left: chartPosition.left - parentPosition.left + parentComponent.scrollLeft,
  };
};

const getTooltipPosition = (
  context,
  tooltipEl,
  tooltipModel,
  marginFromCaret,
  verticalCenterTooltip,
  parentComponent
) => {
  const _marginFromCaret = marginFromCaret || DEFAULT_CHART_TOOLTIP_MARGIN;
  const canvas = context.chart.canvas;
  const chartPosition = canvas.getBoundingClientRect();
  let tooltipWidth;
  const { height: tooltipHeight, width } = tooltipEl.getBoundingClientRect();
  if (width >= chartPosition.width) {
    tooltipWidth = tooltipModel.width;
  } else {
    tooltipWidth = width;
  }

  const { top: chartTop, left: chartLeft } = getChartOrigin(
    chartPosition,
    parentComponent
  );
  const tooltipTop = getTooltipTop(
    chartTop,
    chartPosition.height,
    tooltipHeight,
    tooltipModel.caretY,
    verticalCenterTooltip,
    _marginFromCaret
  );
  const tooltipLeft = getTooltipLeft(
    chartLeft,
    chartPosition.width,
    tooltipWidth,
    tooltipModel.caretX,
    _marginFromCaret
  );
  return [tooltipTop, tooltipLeft];
};

export const getTooltipGenerator =
  (
    tooltipElementId,
    innerHTMLGenerator,
    verticalCenterTooltip = false,
    marginSides,
    parentComponentId
  ) =>
  (context) => {
    const isDarkMode = getIsDarkMode();
    const tooltipClasses = `chart-js-tooltip-wrapper${
      isDarkMode ? " dark-mode" : ""
    }`;
    let tooltipEl = document.getElementById(tooltipElementId);
    const parentComponent =
      document.getElementById(parentComponentId) || document.body;
    // Create element on first render
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = tooltipElementId;
      tooltipEl.dataset.testid = CHART_TOOLTIP_ID;
      tooltipEl.innerHTML = `<div class="${tooltipClasses}"></div>`;
      parentComponent.appendChild(tooltipEl);
    } else if (tooltipEl.parentElement !== parentComponent) {
      parentComponent.appendChild(tooltipEl);
    }

    // Hide if no tooltip
    let tooltipModel = context.tooltip;
    if (tooltipModel.opacity === 0) {
      deactivateChartTooltip(tooltipEl);
      return;
    }

    activateChartTooltip(context.chart, tooltipEl);

    // Set caret Position
    tooltipEl.classList.remove("above", "below", "no-transform");
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
      tooltipEl.classList.add("no-transform");
    }

    // Set Text
    if (tooltipModel.body) {
      var tableRoot = tooltipEl.querySelector(".chart-js-tooltip-wrapper");
      tableRoot.innerHTML = innerHTMLGenerator(context);
    }

    const [top, left] = getTooltipPosition(
      context,
      tooltipEl,
      tooltipModel,
      marginSides,
      verticalCenterTooltip,
      parentComponent
    );
    tooltipEl.style.opacity = 1;
    tooltipEl.style.position = "absolute";
    tooltipEl.style.display = "block";
    tooltipEl.style.left = left + "px";
    tooltipEl.style.top = top + "px";
    tooltipEl.style.padding =
      tooltipModel.padding + "px " + tooltipModel.padding + "px";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.zIndex = 1;
    tooltipEl.dataset.caretX = tooltipModel.caretX;
    tooltipEl.dataset.caretY = tooltipModel.caretY;
  };

export const lineChartTooltipInnerHtmlGenerator =
  (currencyIcon, isMultiDatasets) => (context) => {
    const dataPoints = context.tooltip?.dataPoints;
    const getPointDisplay = (dataPoint) => {
      const dataset = dataPoint?.dataset;
      const { change, suffix, isNegative } = dataPoint?.raw;
      let { y: value } = dataPoint?.raw;
      if (value > 0 && isNegative) value = -value;
      const parsedValue = getLocaleStringWithPrecision(value);
      const changeClassName =
        change >= 0
          ? "chart_tooltip__change__positive"
          : "chart_tooltip__change__negative";
      const arrow =
        change >= 0
          ? renderToString(<ChangeArrowUp />)
          : renderToString(<ChangeArrowDown />);
      return `
        <div class="chart_tooltip__value_change_wrapper">
          <div class="chart_tooltip__flex">
            ${
              isMultiDatasets
                ? `<div class="chart_tooltip__dataset_circle" style="background-color:${safeCssColor(
                    dataset?.borderColor
                  )}"></div>`
                : ""
            }
            <div class="chart_tooltip__value">
              ${currencyIcon}${escapeHtml(parsedValue)}${escapeHtml(
        suffix || ""
      )}
            </div>
          </div>
          ${
            isNaN(Number(change))
              ? ""
              : `
            <div class="chart_tooltip__change ${changeClassName}">
              ${escapeHtml(change)}%
              <span class="chart_tooltip__change_arrow">
                ${arrow} 
              </span>
            </div>
          `
          }
        </div>
      `;
    };

    let pointsDisplay = ``;
    dataPoints?.forEach(
      (dataPoint) =>
        (pointsDisplay = pointsDisplay + getPointDisplay(dataPoint))
    );
    return `
    <div>
      ${pointsDisplay}
      <div class="chart_tooltip__date">${escapeHtml(
        formatDate(dataPoints?.[0]?.raw?.x, true, false)
      )}</div>
    </div>`;
  };

export const pieChartTooltipInnerHtmlGenerator = (context) => {
  const { dataset, dataIndex } = getDataPointFromTooltipContext(context);
  const { name, value, color } = dataset?.rawData[dataIndex];
  const width = String(name ?? "").length > 12 ? "120px" : "auto";
  return `
    <div>
      <div class="chart_tooltip__color_name_wrapper">
        <div class="chart_tooltip__coin_color" style="--background:${safeCssColor(
          color
        )};"></div>
        <div class="chart_tooltip__name_percent_wrapper">
          <div class="chart_tooltip__coin_name" style="width:${width};">
            ${escapeHtml(name)}
          </div>
          <div class="chart_tooltip__percent">${escapeHtml(value)}%</div>
        </div>
      </div>
    </div>
  `;
};
